import React, { useEffect, useState } from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import "./ProgramLifecycleActionModal.css";

/**
 * ProgramLifecycleActionModal — single-step confirm modal for corp-program
 * lifecycle actions (SUSPEND + REACTIVATE per Story 1 CORP-PARTNERSHIP-
 * SUSPEND-REACTIVATE-001; STORY 2 HARD-CANCEL wizard is a separate
 * component landing later). Follows Zuri's visual spec sections 2
 * (Suspend) and 4 (Reactivate): single-step confirmation, no wizard
 * chrome, optional reason textarea on the suspend variant only.
 *
 * The modal is variant-driven so we do not have two 90%-duplicate
 * components. `variant` gates:
 *   - Title / body copy
 *   - Whether the reason textarea renders (suspend only)
 *   - The confirm-button label + color intent (gold suspend / green
 *     reactivate)
 *
 * Reason validation per Zuri §2.2 + ADR-046 D8 precedent: reason field
 * on suspend is OPTIONAL, but if the operator types >= 1 char it MUST
 * be >= 10 chars (audit-log usefulness threshold). BE also enforces this;
 * FE mirrors so the operator gets immediate feedback.
 *
 * Props:
 *  - isOpen: boolean
 *  - variant: 'suspend' | 'reactivate'
 *  - programName: string
 *  - onClose: () => void
 *  - onConfirm: ({ reason?: string }) => void
 *  - inFlight: boolean
 *  - error: string | null           BE error message to surface inline
 */
const VARIANT_COPY = {
  suspend: {
    title: (name) => `Suspend ${name}?`,
    body:
      "New corp-rate bookings and sponsored sessions will be blocked until reactivated. Existing enrollments, bookings, and credits are unchanged. You can reactivate at any time.",
    confirmLabel: "Suspend",
    confirmClass: "program-lifecycle-modal-confirm is-suspend",
    showReason: true,
  },
  reactivate: {
    title: (name) => `Reactivate ${name}?`,
    body:
      "Corp-rate bookings and sponsored sessions will resume immediately. Enrolled employees and family members regain access on save.",
    confirmLabel: "Reactivate",
    confirmClass: "program-lifecycle-modal-confirm is-reactivate",
    showReason: false,
  },
};

const ProgramLifecycleActionModal = ({
  isOpen,
  variant,
  programName,
  onClose,
  onConfirm,
  inFlight = false,
  error = null,
}) => {
  const [reason, setReason] = useState("");

  // Reset local state each time the modal opens so it never leaks state
  // between a suspend and a subsequent reactivate (or the same variant
  // reopened after error dismiss).
  useEffect(() => {
    if (isOpen) {
      setReason("");
    }
  }, [isOpen, variant]);

  // ESC closes when not in-flight (mirrors LinkFamilyMemberModal
  // convention on the deployed page).
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (event) => {
      if (event.key === "Escape" && !inFlight) {
        onClose?.();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, inFlight, onClose]);

  if (!isOpen) return null;

  const copy = VARIANT_COPY[variant] || VARIANT_COPY.suspend;
  const displayName = programName || "this program";

  const trimmedReason = reason.trim();
  const reasonInvalid =
    copy.showReason && trimmedReason.length > 0 && trimmedReason.length < 10;
  const canConfirm = !inFlight && !reasonInvalid;

  const handleBackdropClick = () => {
    if (inFlight) return;
    onClose?.();
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    const payload = {};
    if (copy.showReason && trimmedReason.length >= 10) {
      payload.reason = trimmedReason;
    }
    onConfirm?.(payload);
  };

  return (
    <div
      className="program-lifecycle-modal-backdrop"
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        className="program-lifecycle-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="program-lifecycle-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="program-lifecycle-modal-header">
          <h2
            id="program-lifecycle-modal-title"
            className="program-lifecycle-modal-title"
          >
            {copy.title(displayName)}
          </h2>
        </div>

        <p className="program-lifecycle-modal-body">{copy.body}</p>

        {error ? (
          <div
            className="program-lifecycle-modal-error"
            role="alert"
          >
            <span
              className="program-lifecycle-modal-error-icon"
              aria-hidden="true"
            >
              <FaExclamationTriangle />
            </span>
            <div className="program-lifecycle-modal-error-body">
              <strong className="program-lifecycle-modal-error-title">
                {variant === "suspend"
                  ? "Suspend failed"
                  : "Reactivate failed"}
              </strong>
              <p className="program-lifecycle-modal-error-text">{error}</p>
            </div>
          </div>
        ) : null}

        {copy.showReason ? (
          <>
            <label
              htmlFor="program-lifecycle-reason-input"
              className="program-lifecycle-modal-label"
            >
              Reason (optional)
            </label>
            <p className="program-lifecycle-modal-helper">
              Add context for the audit log. Minimum 10 characters if
              provided.
            </p>
            <textarea
              id="program-lifecycle-reason-input"
              className={`program-lifecycle-modal-textarea${
                reasonInvalid ? " is-invalid" : ""
              }`}
              rows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="e.g. Contract not renewed for Q3; program on hold pending renewal."
              disabled={inFlight}
              aria-invalid={reasonInvalid || undefined}
              aria-describedby={
                reasonInvalid
                  ? "program-lifecycle-reason-error"
                  : undefined
              }
            />
            {reasonInvalid ? (
              <p
                id="program-lifecycle-reason-error"
                className="program-lifecycle-modal-field-error"
                role="alert"
              >
                Reason must be at least 10 characters or left blank.
              </p>
            ) : null}
          </>
        ) : null}

        <div className="program-lifecycle-modal-actions">
          <button
            type="button"
            className="program-lifecycle-modal-cancel"
            onClick={onClose}
            disabled={inFlight}
          >
            Cancel
          </button>
          <button
            type="button"
            className={copy.confirmClass}
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {inFlight ? "Working..." : copy.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgramLifecycleActionModal;
